import React, { useEffect, useState } from 'react';
import { 
  Table, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableBody, 
  Paper,
  Typography,
  Box,
  Chip,
  IconButton,
  Collapse,
  CircularProgress,
  Card,
  CardContent
} from '@mui/material';
import { styled } from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { getProcedureReports, getProcedureResults } from '../../services/procedureResultService';
import { useUserData } from '../../hooks/useUserData';

// Styled Components - Purple/Blue Gradient Header
const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(3),
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
}));

function Row({ report }) {
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchResults = async () => {
    if (!open) {
      setLoading(true);
      try {
        // Use service layer to fetch results
        const resultsData = await getProcedureResults(report.procedure_report_id);
        // Ensure resultsData is always an array
        setResults(Array.isArray(resultsData) ? resultsData : []);
      } catch (error) {
        console.error('Error fetching results:', error);
        setResults([]); // Set empty array on error
      } finally {
        setLoading(false);
      }
    }
    setOpen(!open);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'final':
        return 'success';
      case 'preliminary':
        return 'warning';
      case 'corrected':
        return 'info';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getAbnormalColor = (flag) => {
    if (!flag) return 'default';
    switch (flag.toUpperCase()) {
      case 'H':
      case 'HH':
      case 'L':
      case 'LL':
        return 'warning';
      case 'C':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <>
      <TableRow>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={fetchResults}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{report.procedure_order_id}</TableCell>
        <TableCell>{new Date(report.date_collected).toLocaleString()}</TableCell>
        <TableCell>{new Date(report.date_report).toLocaleString()}</TableCell>
        <TableCell>{report.provider_id}</TableCell>
        <TableCell>
          <Chip 
            label={report.review_status} 
            color={getStatusColor(report.review_status)}
            size="small"
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box margin={1}>
              {loading ? (
                <Box display="flex" justifyContent="center" p={2}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  {report.comments && (
                    <Box mb={2}>
                      <Typography variant="subtitle2" gutterBottom component="div">
                        Comments
                      </Typography>
                      <Typography variant="body2">{report.comments}</Typography>
                    </Box>
                  )}
                  <Typography variant="h6" gutterBottom component="div">
                    Results
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Test</TableCell>
                        <TableCell>Result</TableCell>
                        <TableCell>Units</TableCell>
                        <TableCell>Reference Range</TableCell>
                        <TableCell>Flag</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Array.isArray(results) && results.map((result) => (
                        <TableRow key={result.id}>
                          <TableCell>{result.test_code}</TableCell>
                          <TableCell>{result.result}</TableCell>
                          <TableCell>{result.units}</TableCell>
                          <TableCell>{result.ref_range}</TableCell>
                          <TableCell>
                            <Chip 
                              label={result.abnormal || 'N'} 
                              color={getAbnormalColor(result.abnormal)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={result.result_status} 
                              color={getStatusColor(result.result_status)}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function Results() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const { defaultFacility } = useUserData();

  useEffect(() => {
    const fetchReports = async () => {
      try {
        if (!defaultFacility?.id) {
          console.warn('No default facility available');
          setReports([]);
          setLoading(false);
          return;
        }

        const reportsData = await getProcedureReports({ facilityId: defaultFacility.id });
        // Ensure reportsData is always an array
        setReports(Array.isArray(reportsData) ? reportsData : []);
      } catch (error) {
        console.error('Error fetching reports:', error);
        setReports([]); // Set empty array on error
      } finally {
        setLoading(false);
      }
    };

    if (defaultFacility?.id) {
      fetchReports();
    }
  }, [defaultFacility?.id]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (!defaultFacility?.id) {
    return (
      <Box sx={{ p: 3 }}>
        <HeaderCard>
          <CardContent sx={{ py: 1.5, px: 3, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
              Procedure Results
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              View and manage procedure results and reports
            </Typography>
          </CardContent>
        </HeaderCard>
        
        <Paper sx={{ width: '100%', overflow: 'hidden', p: 3 }}>
          <Typography variant="h6" color="text.secondary" align="center">
            No facility available. Please contact your administrator to set up facility access.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <HeaderCard>
        <CardContent sx={{ py: 1.5, px: 3, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
            Procedure Results
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
            View and manage procedure results and reports
          </Typography>
        </CardContent>
      </HeaderCard>
      
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <Box p={2}>
          <Typography variant="h5" gutterBottom>
            Lab Results
          </Typography>
        </Box>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Order ID</TableCell>
              <TableCell>Collection Date</TableCell>
              <TableCell>Report Date</TableCell>
              <TableCell>Provider</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.isArray(reports) && reports.map((report) => (
              <Row key={report.id} report={report} />
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
} 